import type { ChecklistAnswer } from './types'

// The CanadaGAP Form G and J checklist items, in the form's own wording. Copied from
// server/src/apps/farm_logs/building_logs/vocab.ts (the app doesn't import server code); if the two
// drift apart the API rejects unknown keys with a 422, so keep them in step.

export type ChecklistItem = { key: string; section: string; label: string }
export type ChecklistSection = { key: string; title: string }

export const BUILDING_SECTIONS: ChecklistSection[] = [
  { key: 'interior', title: 'Interior of building (permanent structures)' },
  { key: 'exterior', title: 'Exterior of building (permanent structures)' },
  { key: 'non_permanent', title: 'Exterior of building (non-permanent structures)' },
]

export const BUILDING_CHECKLIST: ChecklistItem[] = [
  { key: 'interior_no_holes', section: 'interior', label: 'No holes/crevices/leaks in the building (e.g., walls, windows, screens)' },
  { key: 'interior_lighting', section: 'interior', label: 'Lighting is shatterproof and adequate' },
  { key: 'interior_no_pipe_leaks', section: 'interior', label: 'No pipes or condensation leaking' },
  { key: 'interior_floor_drainage', section: 'interior', label: 'Floor drainage is good (floor sloped, drain covers clear)' },
  { key: 'interior_surfaces_clean', section: 'interior', label: 'Floors, walls and ceilings are clean and free from garbage, spills, rodent droppings, etc.' },
  { key: 'interior_floor_no_crevices', section: 'interior', label: 'Floor is free of crevices that could harbour pests or debris' },
  { key: 'interior_fans_filters', section: 'interior', label: 'Fans and/or air filters are dust-free, clean and working properly' },
  { key: 'interior_no_pests', section: 'interior', label: 'Animals (wild or domestic), pests and bird nests are not present' },
  { key: 'interior_materials_in_place', section: 'interior', label: 'All materials are in designated areas (e.g., packaging materials and product)' },
  { key: 'interior_ventilation', section: 'interior', label: 'Adequate ventilation' },
  { key: 'interior_cross_contamination', section: 'interior', label: 'Control measures are in place to prevent cross-contamination from other activities/items' },
  { key: 'exterior_no_holes', section: 'exterior', label: 'No holes/crevices/leaks in the building (e.g., walls, windows, screens)' },
  { key: 'exterior_windows', section: 'exterior', label: 'All windows can be closed OR have close-fitting screens in good condition' },
  { key: 'exterior_perimeter_strip', section: 'exterior', label: '½ meter wide perimeter strip of stone or crushed gravel OR short grass around building' },
  { key: 'exterior_no_junk', section: 'exterior', label: 'No junk piled within 3 m of building (e.g., old or unused machinery, garbage)' },
  { key: 'exterior_weeds', section: 'exterior', label: 'Weeds are controlled' },
  { key: 'exterior_land_drainage', section: 'exterior', label: 'Land drainage around building is good' },
  { key: 'exterior_dumpsters', section: 'exterior', label: 'Dumpsters are emptied as needed to prevent pest infestation, and surroundings are free of debris' },
  { key: 'exterior_doors_close_fitting', section: 'exterior', label: 'All doors are close-fitting' },
  { key: 'exterior_doors_securable', section: 'exterior', label: 'Doors that can be secured (i.e., to lock storages when unsupervised)' },
  { key: 'non_permanent_cover', section: 'non_permanent', label: 'Roof or Cover (i.e., tarp)' },
  { key: 'non_permanent_no_harbourage', section: 'non_permanent', label: 'No areas where pests can live/feed/hide within 3 m of structure' },
]

export const HYGIENE_SECTIONS: ChecklistSection[] = [{ key: 'supplies', title: 'Items to inspect for' }]

export const HYGIENE_FACILITY_CHECKLIST: ChecklistItem[] = [
  { key: 'paper_towels', section: 'supplies', label: 'Disposable paper towels' },
  { key: 'soap', section: 'supplies', label: 'Soap' },
  { key: 'water_operating', section: 'supplies', label: 'Water source operating (hot and/or cold water)' },
  { key: 'toilet_paper', section: 'supplies', label: 'Toilet paper' },
  { key: 'hand_sanitizer', section: 'supplies', label: 'Hand sanitizer / wipes' },
  { key: 'garbage_emptied', section: 'supplies', label: 'Garbage emptied' },
]

/** Form G's maintenance columns are Interior and Exterior; non-permanent structures are exterior. */
export const maintenanceSide = (section: string): 'interior' | 'exterior' => (section === 'interior' ? 'interior' : 'exterior')

export function countAnswers(items: ChecklistItem[], checklist: Partial<Record<string, ChecklistAnswer>>) {
  const answers = items.map((i) => checklist[i.key])
  return {
    total: items.length,
    answered: answers.filter(Boolean).length,
    ok: answers.filter((a) => a === 'ok').length,
    issues: answers.filter((a) => a === 'not_ok').length,
  }
}
